import { Component, computed, ContentChild, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, signal, ViewChild } from '@angular/core';
import { Swiper } from 'swiper/types';
import { merge } from 'lodash';
import { SwiperContentDirective } from '../../directives/swiper-content.directive';
import { register as registerSwiper } from 'swiper/element/bundle';

@Component({
    selector: 'lib-swiper',
    templateUrl: './swiper.component.html',
    styleUrls: ['./swiper.component.scss'],
    standalone: false
})
export class SwiperComponent implements OnDestroy {

  @ContentChild(SwiperContentDirective) swiperContent!: SwiperContentDirective;

  @ViewChild('buttonPrev') buttonPrev: ElementRef;
  @ViewChild('buttonNext') buttonNext: ElementRef;
  @ViewChild('swiperContainerRef') set swiperContainerRef(ref: ElementRef) {
    this._swiperContainerRef = ref;
    this.initSwiper(ref)
  };

  public get swiperContainerRef() {
    return this._swiperContainerRef;
  }

  @Input({ alias: 'slides' }) set slidesInput(value: any[]) {
    this.slides.set(value ?? []);
  }

  @Input() isRtl?: boolean;

  @Input({ alias: 'performanceMode' }) set performanceModeInput(value: boolean) {
    this.performanceMode.set(value);
  }

  @Input() set options(options: any) {
    this._options = merge({}, this._options, options);
  }

  @Output() public slideIndexChange: EventEmitter<number> = new EventEmitter();
  @Output() public changed: EventEmitter<void> = new EventEmitter();

  get options(): any {
    return this._options;
  }

  @Output() reachEnd: EventEmitter<void> = new EventEmitter();

  public swiper: Swiper;

  public canSlide = signal(false);

  public slides = signal([]);
  public activeSlide = signal(0);
  public performanceMode = signal(false);
  public visibleCount = signal(1);

  readonly renderedIndexes = computed(() => {
    const total = this.slides()?.length ?? 0;

    if (!this.performanceMode()) {
      return new Set(Array.from({ length: total }, (_, i) => i));
    }

    const current = this.activeSlide();
    const range = this.visibleCount();

    const start = Math.max(0, current - range);
    const end = Math.min(total - 1, current + range);

    const indexes = new Set<number>();

    for (let i = start; i <= end; i++) {
      indexes.add(i);
    }

    return indexes;
  });

  private _options = {
    spaceBetween: 20,
    touchAngle: 75,
    navigation: {
      enabled: false,
      navigationDisabledClass: 'swiper-navigation-disabled'
    }
  };

  private slideChanged = () => {
    this.updateSwiperState();
    this.slideIndexChange.emit(this.swiper.activeIndex);
  }

  private syncSwiperState = () => {
    this.updateSwiperState();
    this.canSlide.set(this.swiper.maxTranslate() !== this.swiper.minTranslate());
  }

  private emitReachEnd = () => this.reachEnd.emit();

  private _swiperContainerRef: ElementRef;

  constructor(private readonly zone: NgZone) {
    this.zone.runOutsideAngular(() => {
      registerSwiper();
    });
  }

  public onReachEnd(): void {
    this.reachEnd.emit();
  }

  ngOnDestroy(): void {
    this.removeSubscriptions();
  }

  private removeSubscriptions(): void {
    if (this.swiper) {
      this.swiper.off('slideChange', this.slideChanged);
      this.swiper.off('reachEnd', this.emitReachEnd);
      this.swiper.off('update', this.syncSwiperState);
      this.swiper.off('resize', this.syncSwiperState);
      this.swiper.off('slidesLengthChange', this.syncSwiperState);
      this.swiper.off('breakpoint', this.syncSwiperState);
    }
  }

  private initSwiper(ref: ElementRef): void {
    if (!ref) {
      return;
    }

    this.removeSubscriptions();

    this.swiper = ref.nativeElement.swiper;
    this.swiper.params = Object.assign(this.swiper.params, this.options);

    this.swiper.on('slideChange', this.slideChanged);
    this.swiper.on('reachEnd', this.emitReachEnd);

    this.swiper.on('update', this.syncSwiperState);
    this.swiper.on('resize', this.syncSwiperState);
    this.swiper.on('slidesLengthChange', this.syncSwiperState);
    this.swiper.on('breakpoint', this.syncSwiperState);

    this.syncSwiperState();
    this.slideChanged();

    if (this.isRtl) {
      this.swiper.changeLanguageDirection('rtl');
    }
  }

  private updateSwiperState() {
    const swiper = this.swiperContainerRef.nativeElement.swiper;
  
    if (!swiper) {
      return;
    }

    this.activeSlide.set(swiper.activeIndex);

    let slidesPerView = swiper.params.slidesPerView;
    const carouselGridRows = swiper.params?.grid?.rows ?? 1;

    if (slidesPerView === 'auto') {
      const firstSlide = swiper.slides[0];
      const slideWidth = firstSlide ? firstSlide.swiperSlideSize : swiper.width;
      slidesPerView = Math.ceil(swiper.width / slideWidth);
    } 
    
    const visibleCount = Math.max(slidesPerView, carouselGridRows);
    this.visibleCount.set(visibleCount);
  }
}
